import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { API } from "@/lib/api";

const ANON_KEY = "hbm.anonId";
const SESSION_KEY = "hbm.sessionId";
const SESSION_TS_KEY = "hbm.sessionTs";
const SESSION_TTL_MS = 30 * 60 * 1000; // 30 min inactivity → new session

function getOrCreateAnonId() {
  try {
    let id = localStorage.getItem(ANON_KEY);
    if (!id) {
      id =
        (crypto?.randomUUID && crypto.randomUUID()) ||
        Math.random().toString(36).slice(2) + Date.now().toString(36);
      localStorage.setItem(ANON_KEY, id);
    }
    return id;
  } catch {
    return "";
  }
}

function getOrCreateSessionId() {
  try {
    const now = Date.now();
    const lastTs = parseInt(sessionStorage.getItem(SESSION_TS_KEY) || "0", 10);
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id || now - lastTs > SESSION_TTL_MS) {
      id =
        (crypto?.randomUUID && crypto.randomUUID()) ||
        Math.random().toString(36).slice(2) + now.toString(36);
      sessionStorage.setItem(SESSION_KEY, id);
    }
    sessionStorage.setItem(SESSION_TS_KEY, String(now));
    return id;
  } catch {
    return "";
  }
}

// ─── Click capture (session replay) ────────────────────────────────────
let _clickListenerAttached = false;

function attachClickListener() {
  if (_clickListenerAttached) return;
  _clickListenerAttached = true;
  const handler = (e) => {
    try {
      const t = e.target;
      if (!t || typeof t.tagName !== "string") return;
      const tag = t.tagName.toLowerCase();
      // Focus on meaningful UI elements to keep the noise down.
      // We DELIBERATELY exclude input / textarea / select so we never
      // touch typed values or placeholders — matches the Privacy Policy
      // promise that values you type are never captured.
      if (!["button", "a", "label", "svg"].includes(tag)) return;
      const anonId = getOrCreateAnonId();
      const sessionId = getOrCreateSessionId();
      const rect = { w: window.innerWidth || 1, h: window.innerHeight || 1 };
      const x = Math.max(0, Math.min(1, (e.clientX || 0) / rect.w));
      const y = Math.max(0, Math.min(1, (e.clientY || 0) / rect.h));
      const label = (t.innerText || t.getAttribute?.("aria-label") || t.getAttribute?.("data-testid") || "")
        .toString()
        .replace(/\s+/g, " ")
        .trim()
        .slice(0, 60);
      const target = `${tag}${t.id ? `#${t.id}` : ""}${
        t.getAttribute?.("data-testid") ? `[${t.getAttribute("data-testid")}]` : ""
      }`.slice(0, 120);
      fetch(`${API}/track/click`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-Anon-Id": anonId,
        },
        body: JSON.stringify({
          session_id: sessionId,
          path: window.location.pathname + window.location.search,
          target,
          x,
          y,
          text: label,
        }),
        keepalive: true,
      }).catch(() => {});
    } catch {
      /* never break the app */
    }
  };
  document.addEventListener("click", handler, { capture: true, passive: true });
}

/**
 * Fires a page-view beacon on every route change + captures clicks for
 * anonymised session replay. Runs once mounted at the router level.
 */
export default function usePageTracking() {
  const location = useLocation();
  const lastPath = useRef(null);
  const referrerAtMount = useRef(document.referrer || "");

  useEffect(() => {
    attachClickListener();
  }, []);

  useEffect(() => {
    const path = location.pathname + (location.search || "");
    if (lastPath.current === path) return;
    const isFirst = lastPath.current === null;
    const referrer = isFirst ? referrerAtMount.current : lastPath.current || "";
    lastPath.current = path;

    const anonId = getOrCreateAnonId();
    const sessionId = getOrCreateSessionId();
    const body = JSON.stringify({ path, referrer, session_id: sessionId });

    try {
      fetch(`${API}/track/pageview`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-Anon-Id": anonId,
        },
        body,
        keepalive: true,
      }).catch(() => {});
    } catch {
      /* never break the app on tracking */
    }
  }, [location.pathname, location.search]);
}
